/* eslint-disable camelcase */
import {
  type Cart,
  EShippingMethod,
  EShippingType
} from '@/modules/cart/domain/Cart';
import type {
  IBasketSummary,
  THomeDeliveryOrderConfirmation,
  TOrderConfirmationDelivery,
  TPickupDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import { SendBuyNowEvents } from '@/modules/shared/utils/gtmEvents';
import type {
  GtmArrayItem,
  GtmArrayItems
} from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import {
  BuyNowEventInteractiveCategories,
  EEventName
} from '@/modules/shared/utils/gtmEvents/buyNowTypes';
import { EAnalyticsShippingMethodType } from '@/modules/shared/utils/gtmEvents/types';

type AttrValue =
  | string
  | number
  | boolean
  | string[]
  | { key?: string; label?: string }
  | null
  | undefined;

const toStringAttr = (v: AttrValue): string => {
  if (v == null) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.map(toStringAttr).filter(Boolean).join(', ');
  if (typeof v === 'object') {
    return v.label || v.key || '';
  }
  return '';
};

const toBoolAttr = (v: AttrValue): boolean => {
  if (typeof v === 'boolean') return v;
  return false;
};

const getDeliveryType = (cart: Cart, sku: string): string => {
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups || [];
  const productShippingMethodGroup = deliveryGroups?.find((group) =>
    group?.products?.map((productId) => productId).includes(sku)
  );
  const option = productShippingMethodGroup?.shippingOption;

  if (!option) return '';

  const shippingType = option?.shippingType;
  const shippingMethod = option?.shippingMethod;

  const isCollect = shippingType === EShippingType.COLLECT;
  const isDelivery = shippingType === EShippingType.DELIVERY;

  if (isCollect) return EAnalyticsShippingMethodType.COLLECT;
  if (isDelivery) {
    const isDateRange = shippingMethod === EShippingMethod.DATE_RANGE;
    const isExpress = shippingMethod === EShippingMethod.NEXT_DAY;
    const isScheduled = shippingMethod === EShippingMethod.SCHEDULED;

    if (isScheduled) return EAnalyticsShippingMethodType.SCHEDULED;
    if (isDateRange) return EAnalyticsShippingMethodType.DATE_RANGE;
    if (isExpress) return EAnalyticsShippingMethodType.NEXT_DAY;
  }

  return '';
};

type TDeliveryWithShipping = THomeDeliveryOrderConfirmation | TPickupDelivery;

// confirma que deliveryDetails tiene shippingServiceName
function hasShippingServiceName(
  d: TOrderConfirmationDelivery
): d is TDeliveryWithShipping {
  return 'shippingServiceName' in d;
}

const getDeliveryTypeFromBasketSummary = (
  basketSummary: IBasketSummary[],
  sku: string
): string => {
  if (!sku || !Array.isArray(basketSummary)) return '';

  let option:
    | { shippingServiceName?: string; shippingMethodType?: string }
    | undefined;

  for (const basket of basketSummary) {
    const foundItem = basket.items.find((item) => item.sku === sku);
    if (
      foundItem &&
      basket.deliveryDetails &&
      hasShippingServiceName(basket.deliveryDetails)
    ) {
      const { shippingServiceName, shippingMethodType } =
        basket.deliveryDetails;
      option = { shippingServiceName, shippingMethodType };
    }
  }

  if (!option) return '';

  const shippingType = option?.shippingMethodType;
  const shippingMethod = option?.shippingServiceName;

  const isCollect = shippingType === 'pickUp';
  const isDelivery = shippingType === 'homeDelivery';

  if (isCollect) return EAnalyticsShippingMethodType.COLLECT;
  if (isDelivery) {
    const isDateRange = shippingMethod === EShippingMethod.DATE_RANGE;
    const isExpress = shippingMethod === EShippingMethod.NEXT_DAY;
    const isScheduled = shippingMethod === EShippingMethod.SCHEDULED;

    if (isScheduled) return EAnalyticsShippingMethodType.SCHEDULED;
    if (isDateRange) return EAnalyticsShippingMethodType.DATE_RANGE;
    if (isExpress) return EAnalyticsShippingMethodType.NEXT_DAY;
  }

  return '';
};

type PricesType = {
  regular?: { value?: { centAmount?: number } };
  offer?: { value?: { centAmount?: number } };
  paymentMethod?: { value?: { centAmount?: number } };
};

const getPrices = (
  prices: PricesType
): { price: number; listPrice: number; cencoPrice: number } => {
  const price = prices?.regular?.value?.centAmount || 0;
  const listPrice = prices?.offer?.value?.centAmount || 0;
  const cencoPrice = prices?.paymentMethod?.value?.centAmount || 0;

  return { price, listPrice, cencoPrice };
};

const getWarranties = (
  cart: Cart,
  sku: string
): { hasWarranty: boolean; warrantyName: string | false } => {
  const productOptions = cart?.productOptions || [];
  let productoOption = null;
  const warrantyNames = [];
  for (const option of productOptions) {
    if (option?.productSku !== sku) continue;
    if (!productoOption) productoOption = option;
    if (option?.addonType === 'warranty') {
      const name = option?.name;
      if (name) warrantyNames.push(name);
    }
  }
  const warrantyName = warrantyNames.length ? warrantyNames.join(',') : '';
  const hasWarranty = warrantyNames.length > 0;

  return { hasWarranty, warrantyName };
};

export const getItemsEventFC = (
  cart: Cart,
  basketSummary: IBasketSummary[]
): GtmArrayItems => {
  const arrayItems: GtmArrayItems = cart?.lineItems?.map((lineItem) => {
    const sku = lineItem?.variant?.sku ?? '';
    const deliveryType =
      basketSummary?.length > 0
        ? getDeliveryTypeFromBasketSummary(basketSummary, sku)
        : getDeliveryType(cart, sku);
    const prices = lineItem?.variant?.prices;
    const { price, listPrice, cencoPrice } = getPrices(prices);
    const { hasWarranty, warrantyName } = getWarranties(cart, sku);
    const attributes = Object.fromEntries(
      lineItem?.variant?.attributes?.map((attr) => [attr.name, attr.value]) ||
        []
    );

    const item: GtmArrayItem = {
      item_id: lineItem?.productKey || '',
      item_name: lineItem?.name || '',
      item_list_name: '', //unmapped
      item_variant: sku,
      item_category: lineItem.productType?.key || '',
      item_brand: toStringAttr(attributes['brand']),
      price: price,
      list_price: listPrice,
      cenco_price: cencoPrice,
      cod_depto: toStringAttr(attributes['departmentId']),
      dpto: toStringAttr(attributes['departmentName']), //unmapped
      cod_base: toStringAttr(attributes['classId']),
      base: toStringAttr(attributes['className']), //unmapped
      cod_subdpto: toStringAttr(attributes['subdepartmentId']),
      subdpto: toStringAttr(attributes['subdepartmentName']), //unmapped
      tipo_envio: deliveryType,
      garantia: hasWarranty,
      nombre_garantia: warrantyName || '',
      seller: lineItem?.seller || '',
      sponsoredproducts: toBoolAttr(attributes['sponsored']),
      quantity: lineItem?.quantity || 1
    };

    return item;
  });

  return arrayItems;
};

export const sendEcommerceEventsForFC = (
  cart: Cart,
  regionAndCommune: { region: string; commune: string }
): void => {
  const items: GtmArrayItems = getItemsEventFC(cart, [] as IBasketSummary[]);
  const { region, commune } = regionAndCommune;

  SendBuyNowEvents.sendEvent({
    event: EEventName.BEGIN_CHECKOUT,
    extraFields: {
      ecommerce: {
        flujo_checkout: BuyNowEventInteractiveCategories.FastCheckout,
        items
      }
    }
  });

  SendBuyNowEvents.sendEvent({
    event: EEventName.ADD_SHIPPING_INFO,
    extraFields: {
      ecommerce: {
        flujo_checkout: BuyNowEventInteractiveCategories.FastCheckout,
        shipping_region: region,
        shipping_comune: commune,
        items
      }
    }
  });

  SendBuyNowEvents.sendEvent({
    event: EEventName.ADD_PAYMENT_INFO,
    extraFields: {
      ecommerce: {
        flujo_checkout: BuyNowEventInteractiveCategories.FastCheckout,
        items
      }
    }
  });
};
